import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Container, Typography, TextField, InputAdornment, Button,
  Chip, Avatar, Card, CardContent, Divider, Paper, CircularProgress,
  Stack, Drawer, IconButton, useMediaQuery, useTheme, alpha,
} from "@mui/material";
import {
  Search as SearchIcon,
  Comment as CommentIcon,
  Share as ShareIcon,
  ThumbUpOffAlt as ThumbUpOutlineIcon,
  Tune as TuneIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Forum as ForumIcon,
} from "@mui/icons-material";
import Footer from "./footer";
import TopBar from "./topbar";

// ── Brand palette ────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",
  secondaryDark: "#0A4A1C",
  secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  secondarySofter: "#F0F8F3",
  bg: "#FAFBFC",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  borderSoft: "#EEF1F6",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
};

const categories = [
  { id: "all", label: "All Discussions" },
  { id: "Technology", label: "Technology" },
  { id: "Manufacturing", label: "Manufacturing" },
  { id: "AI and Art", label: "AI and Art" },
  { id: "Web development", label: "Web Dev" },
];

const tags = ["Startup", "Design help", "Funding", "Collaboration", "Trending"];

// ── Sidebar filter panel (shared by desktop Paper + mobile Drawer) ────────────
function FilterPanel({ selectedCategory, onCategory, selectedTags, onTag }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", mb: 1 }}>
        Categories
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
        {categories.map((cat) => {
          const active = selectedCategory === cat.id;
          return (
            <Chip
              key={cat.id}
              label={cat.label}
              onClick={() => onCategory(cat.id)}
              sx={{
                bgcolor: active ? C.primary : C.borderSoft,
                color: active ? "#fff" : C.textSecondary,
                fontWeight: active ? 700 : 500,
                fontSize: 12,
                border: "none",
                "&:hover": { bgcolor: active ? C.primaryDark : C.primarySoft, color: active ? "#fff" : C.primary },
              }}
            />
          );
        })}
      </Box>

      <Divider sx={{ mb: 2.5, borderColor: C.borderSoft }} />

      <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textMuted, letterSpacing: 1, textTransform: "uppercase", mb: 1 }}>
        Tags
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {tags.map((tag) => {
          const active = selectedTags.includes(tag);
          return (
            <Chip
              key={tag}
              label={tag}
              onClick={() => onTag(tag)}
              variant={active ? "filled" : "outlined"}
              sx={{
                fontSize: 12,
                bgcolor: active ? C.primarySoft : "transparent",
                color: active ? C.primary : C.textMuted,
                borderColor: active ? C.primary : C.border,
                fontWeight: active ? 700 : 500,
                "&:hover": { borderColor: C.primary, color: C.primary, bgcolor: C.primarySofter },
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}

// ── Post card ──────────────────────────────────────────────────────────────────
function PostCard({ post, onClick }) {
  return (
    <Card
      onClick={onClick}
      elevation={0}
      sx={{
        position: "relative",
        borderRadius: 3,
        border: `1px solid ${C.border}`,
        bgcolor: C.paper,
        boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
        mb: 2,
        cursor: "pointer",
        transition: "all 0.2s ease",
        overflow: "hidden",
        "&:hover": {
          boxShadow: `0 6px 20px ${C.primary}15`,
          transform: "translateY(-1px)",
          borderColor: C.primary,
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0, left: 0, right: 0,
          height: "3px",
          background: `linear-gradient(90deg, ${C.primary}, ${C.primaryLight})`,
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        {/* Author row */}
        <Stack direction="row" alignItems="center" spacing={1.25} mb={1.5}>
          <Avatar
            sx={{
              width: 36, height: 36,
              background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
              color: "#fff",
              fontSize: 14, fontWeight: 700,
              boxShadow: `0 3px 8px ${C.primary}25`,
            }}
          >
            {post.createdBy?.email?.charAt(0).toUpperCase() || "U"}
          </Avatar>
          <Box flex={1} minWidth={0}>
            <Typography sx={{ fontWeight: 700, fontSize: 13, color: C.textPrimary }} noWrap>
              {post.createdBy?.email || "Unknown User"}
            </Typography>
            <Chip
              label={post.category}
              size="small"
              sx={{
                height: 18, fontSize: 10, fontWeight: 700,
                bgcolor: C.secondarySoft, color: C.secondary,
                border: "none",
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          </Box>
          <Typography sx={{ fontSize: 11, color: C.textMuted, flexShrink: 0, fontWeight: 500 }}>
            {new Date(post.createdAt).toLocaleDateString()}
          </Typography>
        </Stack>

        {/* Title */}
        <Typography sx={{
          fontWeight: 700, fontSize: { xs: 14, sm: 16 },
          color: C.textPrimary, mb: 0.75, lineHeight: 1.4,
        }}>
          {post.title}
        </Typography>

        {/* Description */}
        <Typography sx={{
          fontSize: 13, color: C.textSecondary, lineHeight: 1.65, mb: 2,
          display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {post.description}
        </Typography>

        {/* Action chips */}
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" gap={0.5}>
          <Chip
            icon={<ThumbUpOutlineIcon sx={{ fontSize: "14px !important" }} />}
            label="Like"
            variant="outlined"
            size="small"
            onClick={(e) => e.stopPropagation()}
            sx={{
              fontSize: 12, fontWeight: 600,
              borderColor: C.border, color: C.textSecondary,
              "&:hover": { borderColor: C.primary, color: C.primary, bgcolor: C.primarySofter },
            }}
          />
          <Chip
            icon={<CommentIcon sx={{ fontSize: "14px !important" }} />}
            label="Comment"
            variant="outlined"
            size="small"
            onClick={(e) => e.stopPropagation()}
            sx={{
              fontSize: 12, fontWeight: 600,
              borderColor: C.border, color: C.textSecondary,
              "&:hover": { borderColor: C.primary, color: C.primary, bgcolor: C.primarySofter },
            }}
          />
          <Box flex={1} />
          <Chip
            icon={<ShareIcon sx={{ fontSize: "14px !important" }} />}
            label="Share"
            variant="outlined"
            size="small"
            onClick={(e) => e.stopPropagation()}
            sx={{
              fontSize: 12, fontWeight: 600,
              borderColor: C.border, color: C.textSecondary,
              "&:hover": { borderColor: C.primary, color: C.primary, bgcolor: C.primarySofter },
            }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function DiscussionForum() {
  const [searchInput, setSearchInput] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedTags, setSelectedTags] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const postsPerPage = 4;

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const res = await fetch("https://backend-global-1vq9.onrender.com/api/posts");
        if (!res.ok) throw new Error("Failed to load posts");
        const data = await res.json();
        setPosts(data.reverse());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const handleCategoryClick = (id) => { setSelectedCategory(id); setCurrentPage(1); };
  const handleTagClick = (tag) => {
    setSelectedTags((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]);
    setCurrentPage(1);
  };

  const filteredPosts = posts.filter((post) => {
    const matchesCategory = selectedCategory === "all" || post.category === selectedCategory;
    const matchesSearch =
      post.title?.toLowerCase().includes(searchInput.toLowerCase()) ||
      post.description?.toLowerCase().includes(searchInput.toLowerCase());
    const matchesTags =
      selectedTags.length === 0 ||
      selectedTags.some((tag) => post.description?.toLowerCase().includes(tag.toLowerCase()));
    return matchesCategory && matchesSearch && matchesTags;
  });

  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const currentPosts = filteredPosts.slice((currentPage - 1) * postsPerPage, currentPage * postsPerPage);
  const activeFiltersCount = (selectedCategory !== "all" ? 1 : 0) + selectedTags.length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
      <TopBar />

      {/* Mobile filter drawer */}
      <Drawer
        anchor="left"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{ sx: { width: 290, p: 2.5 } }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography fontWeight={700} fontSize={16} color={C.textPrimary}>Filters</Typography>
          <IconButton size="small" onClick={() => setFilterDrawerOpen(false)} sx={{ color: C.textMuted }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Divider sx={{ mb: 2.5, borderColor: C.borderSoft }} />
        <FilterPanel
          selectedCategory={selectedCategory}
          onCategory={(id) => { handleCategoryClick(id); setFilterDrawerOpen(false); }}
          selectedTags={selectedTags}
          onTag={handleTagClick}
        />
      </Drawer>

      <Box sx={{ flex: 1, px: { xs: 2, sm: 3, md: 5, lg: 10 }, py: { xs: 3, md: 4 } }}>

        {/* ── Hero header ────────────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            borderRadius: 3, overflow: "hidden",
            background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
            p: { xs: 3, sm: 4 },
            mb: { xs: 2.5, sm: 3 },
            color: "#fff",
            boxShadow: `0 6px 24px ${C.primary}30`,
          }}
        >
          <Box sx={{
            position: "absolute",
            top: "-40%", right: "-10%",
            width: { xs: 200, sm: 280 }, height: { xs: 200, sm: 280 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />

          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}
            sx={{ position: "relative", zIndex: 1 }}>
            <Box flex={1} minWidth={0}>
              <Stack direction="row" alignItems="center" spacing={1.25} mb={1}>
                <ForumIcon sx={{ fontSize: 20, color: "rgba(255,255,255,0.85)" }} />
                <Typography sx={{
                  fontSize: { xs: 11, sm: 12 }, fontWeight: 700, letterSpacing: 1.5,
                  textTransform: "uppercase", color: "rgba(255,255,255,0.75)",
                }}>
                  Discussion Forum
                </Typography>
              </Stack>
              <Typography sx={{
                fontWeight: 800, fontSize: { xs: 22, sm: 28, md: 32 },
                color: "#fff", lineHeight: 1.15, mb: 0.75, letterSpacing: -0.5,
              }}>
                Community
              </Typography>
              <Typography sx={{
                color: "rgba(255,255,255,0.85)",
                fontSize: { xs: 13, sm: 14 }, maxWidth: 540, lineHeight: 1.5,
              }}>
                Discuss, share ideas, and connect with the community
              </Typography>
            </Box>

            <Stack direction={{ xs: "row", sm: "row" }} spacing={1.25} alignItems="center" sx={{ flexShrink: 0 }}>
              {isMobile && (
                <Button
                  variant="contained"
                  startIcon={<TuneIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setFilterDrawerOpen(true)}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.18)",
                    color: "#fff",
                    borderRadius: 2.5, fontWeight: 700, fontSize: 12.5,
                    textTransform: "none",
                    boxShadow: "none",
                    backdropFilter: "blur(6px)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.28)", boxShadow: "none" },
                  }}
                >
                  Filters{activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ""}
                </Button>
              )}

              <Button
                variant="contained"
                startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate("/communityPost")}
                sx={{
                  bgcolor: "#fff", color: C.primary,
                  fontWeight: 700, fontSize: 12.5,
                  textTransform: "none", borderRadius: 2.5,
                  px: 2,
                  boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                  "&:hover": { bgcolor: "#fff", boxShadow: "0 6px 18px rgba(0,0,0,0.2)" },
                }}
              >
                Create Post
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Search bar */}
        <TextField
          fullWidth
          placeholder="Search discussions..."
          value={searchInput}
          onChange={(e) => { setSearchInput(e.target.value); setCurrentPage(1); }}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: C.textMuted }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 3,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2.5, fontSize: 14, bgcolor: C.paper,
              "& fieldset": { borderColor: C.border },
              "&:hover fieldset": { borderColor: C.primary },
              "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: 2 },
            },
          }}
        />

        {/* Two-column layout */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">

          {/* Desktop sidebar */}
          {!isMobile && (
            <Paper
              elevation={0}
              sx={{
                position: "relative",
                width: 270, flexShrink: 0, p: 2.5,
                borderRadius: 3,
                border: `1px solid ${C.border}`,
                bgcolor: C.paper,
                boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
                position: "sticky", top: 24,
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0, left: 0, right: 0,
                  height: "3px",
                  background: `linear-gradient(90deg, ${C.secondary}, ${C.secondary}88)`,
                },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={2} mt={0.5}>
                <Box sx={{
                  width: 4, height: 22,
                  background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
                  borderRadius: 1,
                }} />
                <Typography fontWeight={700} fontSize={14} color={C.textPrimary}>
                  Categories & Filters
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2.5, borderColor: C.borderSoft }} />
              <FilterPanel
                selectedCategory={selectedCategory}
                onCategory={handleCategoryClick}
                selectedTags={selectedTags}
                onTag={handleTagClick}
              />
            </Paper>
          )}

          {/* Posts column */}
          <Box flex={1} minWidth={0}>
            {!loading && !error && (
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
                <Typography fontSize={13} color={C.textSecondary} fontWeight={600}>
                  {filteredPosts.length} post{filteredPosts.length !== 1 ? "s" : ""} found
                </Typography>
                {isMobile && activeFiltersCount > 0 && (
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" gap={0.5}>
                    {selectedCategory !== "all" && (
                      <Chip
                        label={categories.find((c) => c.id === selectedCategory)?.label}
                        size="small"
                        onDelete={() => handleCategoryClick("all")}
                        sx={{
                          fontSize: 11, height: 22,
                          bgcolor: C.primarySoft, color: C.primary, fontWeight: 700,
                          "& .MuiChip-deleteIcon": { color: C.primary },
                        }}
                      />
                    )}
                    {selectedTags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        onDelete={() => handleTagClick(tag)}
                        sx={{
                          fontSize: 11, height: 22,
                          bgcolor: C.primarySoft, color: C.primary, fontWeight: 700,
                          "& .MuiChip-deleteIcon": { color: C.primary },
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Stack>
            )}

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={250}>
                <CircularProgress size={44} thickness={5} sx={{ color: C.primary }} />
              </Box>
            ) : error ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}
                bgcolor={C.paper} borderRadius={3} border={`1px solid ${C.border}`}>
                <Typography sx={{ color: "#DC2626", fontSize: 14, fontWeight: 600 }}>{error}</Typography>
              </Box>
            ) : currentPosts.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center"
                minHeight={220} bgcolor={C.paper} borderRadius={3} border={`1px solid ${C.border}`} py={4}>
                <SearchIcon sx={{ fontSize: 44, color: C.textMuted, opacity: 0.5, mb: 1 }} />
                <Typography fontWeight={700} color={C.textPrimary} fontSize={14}>No posts found</Typography>
                <Typography sx={{ fontSize: 12, color: C.textSecondary, mt: 0.5 }}>
                  Try adjusting your filters or search
                </Typography>
              </Box>
            ) : (
              <>
                {currentPosts.map((post) => (
                  <PostCard
                    key={post._id}
                    post={post}
                    onClick={() => navigate(`/posts/${post._id}`)}
                  />
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mt={3} flexWrap="wrap" gap={2}>
                    <Button
                      variant="outlined"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => p - 1)}
                      sx={{
                        borderRadius: 2.5, borderColor: C.primary, color: C.primary,
                        fontWeight: 700, fontSize: 13, textTransform: "none", px: 2.5,
                        "&:hover": { borderColor: C.primaryDark, color: C.primaryDark, bgcolor: C.primarySoft },
                        "&.Mui-disabled": { borderColor: C.borderSoft, color: C.textMuted },
                      }}
                    >
                      ← Previous
                    </Button>

                    <Stack direction="row" spacing={0.75} alignItems="center">
                      {Array.from({ length: totalPages }, (_, i) => (
                        <Box
                          key={i}
                          onClick={() => setCurrentPage(i + 1)}
                          sx={{
                            width: i + 1 === currentPage ? 24 : 8,
                            height: 8, borderRadius: 1,
                            bgcolor: i + 1 === currentPage ? C.primary : alpha(C.primary, 0.2),
                            cursor: "pointer", transition: "all 0.22s ease",
                          }}
                        />
                      ))}
                    </Stack>

                    <Button
                      variant="outlined"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((p) => p + 1)}
                      sx={{
                        borderRadius: 2.5, borderColor: C.primary, color: C.primary,
                        fontWeight: 700, fontSize: 13, textTransform: "none", px: 2.5,
                        "&:hover": { borderColor: C.primaryDark, color: C.primaryDark, bgcolor: C.primarySoft },
                        "&.Mui-disabled": { borderColor: C.borderSoft, color: C.textMuted },
                      }}
                    >
                      Next →
                    </Button>
                  </Stack>
                )}
              </>
            )}
          </Box>
        </Stack>
      </Box>

      <Footer />
    </Box>
  );
}